import { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { StatusMessage, Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Pagination } from '../../components/uiChrome.jsx';
import { AUDIT_SEVERITY_ORDER, auditSeverityLabelKey } from '../../constants';
import { AuditTable } from '../../components';
import { useProviderAudit } from '../../hooks/provider';
import { AlertTriangle, ArrowRight } from 'lucide-react';

// Top-filter time ranges. Nothing is pre-selected — an empty range means "no
// time cutoff" (all events), so the toolbar starts with no active filter.
const TIME_RANGES = ['24h', '7d', '30d', '90d'];
const DAY = 24 * 60 * 60 * 1000;
const TIME_RANGE_MS = { '24h': DAY, '7d': 7 * DAY, '30d': 30 * DAY, '90d': 90 * DAY };
const DEFAULT_PAGE_SIZE = 10;

/**
 * Provider Detail — Audit tab (CT-4184, admin-only). Mirrors the prototype
 * `detailAudit`: a compact header + a search / time-range / severity `Filters`
 * bar over the reusable {@link AuditTable} (workspace-style table with expandable
 * raw-JSON rows). "View full audit log" opens Global Audit (CT-4210) with
 * `?provider=` pre-applied. Newest first.
 */
const AuditTab = () => {
  const { t } = useTranslation('admin-studio');
  const { provider } = useOutletContext();
  const { rows, now, isLoading, error } = useProviderAudit(provider.slug);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState([]);
  const [timeRange, setTimeRange] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);

  // `Filters` is memo()'d — keep searchProps and the filter config referentially
  // stable so a keystroke doesn't force it (and its inner useMemos) to re-render.
  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (e) => {
        setSearch(e?.target ? e.target.value : e);
        setPage(1);
      },
      onClear: () => {
        setSearch('');
        setPage(1);
      },
      placeholder: t('integrations.audit.searchPlaceholder'),
    }),
    [search, t],
  );

  const filtersConfig = useMemo(
    () => [
      {
        id: 'time',
        label: timeRange[0] ? t(`integrations.audit.time.${timeRange[0]}`) : t('integrations.audit.timeFilter'),
        multiple: false,
        value: timeRange,
        onChange: (next) => {
          setTimeRange(next);
          setPage(1);
        },
        options: TIME_RANGES.map((r) => ({ value: r, label: t(`integrations.audit.time.${r}`) })),
      },
      {
        id: 'severity',
        label: t('integrations.audit.severityFilter'),
        multiple: false,
        value: severityFilter,
        onChange: (next) => {
          setSeverityFilter(next);
          setPage(1);
        },
        options: AUDIT_SEVERITY_ORDER.map((s) => ({ value: s, label: t(auditSeverityLabelKey(s)) })),
      },
    ],
    [timeRange, severityFilter, t],
  );

  const visibleRows = useMemo(() => {
    const range = timeRange[0];
    const cutoff = range ? now - TIME_RANGE_MS[range] : null;
    const q = search.trim().toLowerCase();
    const sev = severityFilter[0];
    return rows.filter((r) => {
      if (cutoff != null && new Date(r.timestamp).getTime() < cutoff) return false;
      if (sev && r.severity !== sev) return false;
      if (q && !`${r.event} ${r.actor?.name ?? ''} ${r.resource ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, timeRange, severityFilter, search, now]);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const pageRows = visibleRows.slice(pageStart, pageStart + perPage);
  const rangeLabel =
    visibleRows.length === 0
      ? '0'
      : `${pageStart + 1}–${Math.min(pageStart + perPage, visibleRows.length)}`;

  return (
    <div className="flex min-h-full flex-1 flex-col gap-3">
      {/* Compact header: title · inline "View full audit log" link + one-line note. */}
      <div>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[13px]">
          <span className="font-semibold text-content">
            {t('integrations.audit.header', { provider: provider.name })}
          </span>
          <span aria-hidden="true" className="text-content-faint">·</span>
          <Link
            to={`/admin-studio/audit?provider=${encodeURIComponent(provider.slug)}`}
            className="inline-flex items-center gap-1 font-medium text-brand-500 hover:underline"
          >
            {t('integrations.audit.viewFull')}
            <ArrowRight size={13} aria-hidden="true" />
          </Link>
        </div>
        <p className="mt-1 text-[12px] leading-snug text-content-muted">— {t('integrations.audit.note')}</p>
      </div>

      <Filters
        searchProps={searchProps}
        filters={filtersConfig}
        showAllFilters={false}
        maxFilters={2}
        onClearAll={() => {
          setSearch('');
          setPage(1);
        }}
      />

      <AuditTable
        rows={pageRows}
        now={now}
        slug={provider.slug}
        emptyLabel={t('integrations.audit.empty')}
      />

      {/* `mt-auto` drops it to the bottom of the flex column; `sticky bottom-0`
          keeps it pinned while a long log scrolls (same pattern as HTL Packs). */}
      <Pagination
        className="mt-auto sticky bottom-0 z-10 bg-surface-base"
        rangeLabel={rangeLabel}
        totalItems={visibleRows.length}
        currentPage={safePage}
        totalPages={totalPages}
        perPage={perPage}
        hasPrevious={safePage > 1}
        hasNext={safePage < totalPages}
        onPrevPage={() => setPage(Math.max(1, safePage - 1))}
        onNextPage={() => setPage(Math.min(totalPages, safePage + 1))}
        onPageSizeChange={(size) => {
          setPerPage(size);
          setPage(1);
        }}
      />
    </div>
  );
};

export default AuditTab;
